import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '../../../../components/ui/alert-dialog'
import { Button } from '../../../../components/ui/Button'

export interface ArchiveSectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Titel des Abschnitts. */
  title: string
  /** Offene Aufgaben, die das Archivieren abhakt (> 0 — ohne kommt keine Rückfrage). */
  openCount: number
  onConfirm: () => void
}

/**
 * **Rückfrage vor dem Archivieren** (#193): Archivieren hakt die offenen Aufgaben des Abschnitts
 * ab, wie in Todoist — das soll niemand aus Versehen tun. Kommt nur, wenn Offenes da ist.
 *
 * @example
 * <ArchiveSectionDialog open={open} onOpenChange={setOpen} title="Packen" openCount={3}
 *   onConfirm={() => archive(sectionId)} />
 */
export const ArchiveSectionDialog: React.FC<ArchiveSectionDialogProps> = ({
  open, onOpenChange, title, openCount, onConfirm,
}) => {
  const { t } = useTranslation()
  const cancelRef = useRef<HTMLButtonElement>(null)
  /*
    Die Knöpfe sind die `Button`s der App, nicht Radix' `Action`/`Cancel`: Deren `asChild` gäbe
    `Button` einen Klick-Handler, den `Button` bei Enter ohne Ereignis ruft — Radix liest daraus
    `defaultPrevented`. Den Anfangsfokus, den Radix sonst auf „Abbrechen“ legt, setzen wir selbst.
  */
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent onOpenAutoFocus={() => cancelRef.current?.focus({ preventScroll: true })}>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('section.archiveConfirm.title', { title })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('section.archiveConfirm.description', { count: openCount })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button ref={cancelRef} variant="secondary" onClick={() => onOpenChange(false)}>
            {t('section.archiveConfirm.cancel')}
          </Button>
          <Button variant="primary" onClick={onConfirm}>{t('section.archiveConfirm.confirm')}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
